import { useRouter } from 'next/router';
import { type FC, useContext, useEffect, useState } from 'react';

import Buttons from '@/components/Buttons';
import CaretRight from '@/components/Icons/CaretRight';
import CheckCircle from '@/components/Icons/CheckCircle';
import CreditCard from '@/components/Icons/CreditCard';
import * as format from '@/format';
import { IDENTIFIERS, getUrl } from '@/helpers/urlsHelper';
import DashboardContext from '@/layouts/DashboardLayout/DashboardContext';
import UserProfileContext from '@/layouts/UserProfileLayout/UserProfileContext';
import SkipTaskConfirmationModal from '@/modals/SkipTaskConfirmationModal';
import { useOpenInvoicePane } from '@/panes/hooks';
import { type PaymentGateRead } from '@/tsClient/servers/noodleApi/ApiDefinition/workflows';
import { skipPaymentGate } from '@/tsClient/workflows';
import { MESSAGE_ATTACHMENT_REFERENCE_TYPES } from '@/typings/api-models';
import { type Currency, DEFAULT_CURRENCY } from '@/typings/common';

const PROGRESS_CIRCUMFERENCE = 138;

const computeProgressRatio = (gate: PaymentGateRead): number => {
  if (gate.status === 'completed' || gate.status === 'skipped') return 1;
  if (gate.gateType === 'minimum' && gate.minimumAmount) {
    return Math.min(gate.invoicePaidAmount / (gate.minimumAmount / 100), 1);
  }
  if (gate.gateType === 'percentage' && gate.minimumPercentage && gate.invoiceTotalAmount) {
    const paidPct = (gate.invoicePaidAmount / gate.invoiceTotalAmount) * 100;
    return Math.min(paidPct / gate.minimumPercentage, 1);
  }
  return 0;
};

const formatCentsWithCurrency = (amountInCents: number, currency: Currency): string =>
  format.price.withCurrency(amountInCents / 100, currency);

const renderSubtext = (gate: PaymentGateRead, currency: Currency): React.ReactNode => {
  if (gate.status === 'skipped') return 'This task is no longer required';
  if (gate.status === 'completed') return 'Minimum requirement met';
  if (gate.gateType === 'minimum' && gate.minimumAmount) {
    return (
      <>
        {format.price.withCurrency(gate.invoicePaidAmount, currency)} of{' '}
        {formatCentsWithCurrency(gate.minimumAmount, currency)} minimum
      </>
    );
  }
  if (gate.gateType === 'percentage' && gate.minimumPercentage) {
    return <>{gate.minimumPercentage}% of total invoice required before proceeding</>;
  }
  return null;
};

/** Circular progress ring rendered around a card icon slot; `ratio` is clamped to [0, 1]. */
const PaymentGateProgressRing: FC<{ ratio: number }> = ({ ratio }) => {
  const dashOffset = (1 - ratio) * PROGRESS_CIRCUMFERENCE;
  return (
    <figure
      role='none'
      className='relative m-0 flex h-9 w-9 min-w-9 items-center justify-center rounded-full border border-(--color-gray-25)'
    >
      <svg className='absolute -rotate-90' width={36} height={36} viewBox='0 0 46 46'>
        <circle
          cx={23}
          cy={23}
          r={22}
          fill='none'
          stroke='var(--color-primary)'
          strokeWidth={2}
          strokeLinecap='round'
          style={{
            strokeDasharray: `${PROGRESS_CIRCUMFERENCE}`,
            strokeDashoffset: `${dashOffset}`,
          }}
        />
      </svg>
      <CreditCard size={20} color='var(--color-gray-100)' />
    </figure>
  );
};

type Props = {
  gate: PaymentGateRead;
  canAccess: boolean;
  canSkip?: boolean;
  onChange?: () => Promise<void>;
  creatorSlug?: string;
  currency?: Currency;
  setIsFulfilled?: (f: boolean) => void;
};

const PaymentGateCard: FC<Props> = ({
  gate,
  canAccess,
  canSkip = false,
  onChange,
  creatorSlug,
  currency = DEFAULT_CURRENCY,
  setIsFulfilled,
}) => {
  const router = useRouter();
  const { creator, isInDashboard } = useContext(DashboardContext);
  const { isInUserProfile } = useContext(UserProfileContext);
  const openInvoicePane = useOpenInvoicePane();
  const [skipTaskModalOpen, setSkipTaskModalOpen] = useState(false);

  const isCompleted = gate.status === 'completed';
  const isSkipped = gate.status === 'skipped';
  const isFulfilled = isCompleted || isSkipped;
  const ratio = computeProgressRatio(gate);

  useEffect(() => {
    setIsFulfilled?.(isFulfilled);
  }, [isFulfilled, setIsFulfilled]);

  const handleSkipTask = async (): Promise<void> => {
    await skipPaymentGate({ paymentGateId: gate.id });
    setSkipTaskModalOpen(false);
    if (onChange) await onChange();
  };

  const handleClick = (): void => {
    if (isInDashboard || isInUserProfile) {
      openInvoicePane({ invoiceId: gate.invoiceId });
      return;
    }
    void router.push(
      getUrl(IDENTIFIERS.INVOICE_DETAIL, {
        creatorSlug: creatorSlug || creator?.slug || (router.query.creatorSlug as string) || '',
        invoiceId: gate.invoiceId,
      }),
    );
  };

  return (
    <>
      {skipTaskModalOpen && (
        <SkipTaskConfirmationModal
          onClose={() => setSkipTaskModalOpen(false)}
          onConfirm={handleSkipTask}
          taskType={MESSAGE_ATTACHMENT_REFERENCE_TYPES.PAYMENT_GATE}
        />
      )}
      <Buttons
        className='flex flex-wrap items-center !gap-3 cursor-pointer rounded-lg !border !border-(--color-gray-25) !bg-(--color-gray-0) !px-3 !py-2 text-(--color-primary) shadow-md'
        key={gate.id}
        isFullWidth
        isWrapper
        onClick={handleClick}
        disabled={!canAccess}
      >
        {isFulfilled ? (
          <CheckCircle
            size={16}
            weight='fill'
            color={isSkipped ? 'var(--color-gray-75)' : 'var(--color-success)'}
            className='shrink-0'
          />
        ) : (
          <PaymentGateProgressRing ratio={ratio} />
        )}
        <div className='flex flex-1 flex-col text-start text-(--color-primary)'>
          <p
            className={isFulfilled ? 'caption-bold' : 'body-sm-bold'}
            style={isFulfilled ? { color: 'var(--color-gray-75)' } : { lineHeight: 1.4 }}
          >
            Payment Required
          </p>
          <p className='caption text-(--color-gray-75)'>{renderSubtext(gate, currency)}</p>
        </div>
        {canAccess && (
          <>
            {isFulfilled ? (
              <CaretRight size={12} color='var(--color-gray-100)' className='shrink-0' />
            ) : (
              <div className='caption flex h-8 w-16 items-center justify-center rounded border border-(--color-gray-25) bg-(--color-gray-0) text-(--color-gray-100) max-md:w-full'>
                View
              </div>
            )}
          </>
        )}
      </Buttons>
      {canSkip && !isFulfilled && (
        <Buttons
          onClick={(event) => {
            event.stopPropagation();
            setSkipTaskModalOpen(true);
          }}
          className='caption !h-6 !bg-[#66666610] text-(--color-gray-75) !border-t !border-(--color-gray-25)'
        >
          Skip
        </Buttons>
      )}
    </>
  );
};

export default PaymentGateCard;
